<template>
  <div ref="pageRef" class="product-detail-page">
    <!-- HEADER -->
    <div class="header" v-if="!isProductDetailLoading">
      <h2 class="header-title">Ürün Detay</h2>
      <p class="subtitle">
        Eklediğiniz ürünün tüm bilgilerini inceleyebilir, belirli alanlarda
        değişiklik yapabilir veya güncelleme talep edebilirsiniz.
      </p>
    </div>
    <div class="header" v-else>
      <h2 class="header-title">
        <Skeleton height="26px" width="113px" borderRadius="4px" />
      </h2>
      <p class="subtitle">
        <Skeleton height="18px" width="755px" borderRadius="4px" />
      </p>
    </div>

    <div class="content-grid">
      <SideBar
        :nutritionInfoRequired="productDetail?.nutritionInfoRequired"
        :productId="pid"
        :activeMenuIndex="activeMenuIndex"
        :isLoading="isProductDetailLoading"
        @menu-changed="handleMenuChange"
        @go-to-reviews="handleGoToReviews"
      />

      <!-- SAĞ İÇERİK -->
      <main class="pd-content">
        <!-- PDP TEMEL BİLGİLER / ÜRÜN & SATIŞ DURUMU -->
        <div ref="productStatusRef">
          <ProductStatus
            :productStatus="productStatusInfo"
            :isLoading="isProductDetailLoading"
            @refetchProductDetail="handleRefetchProductDetail"
            @scrollToSection="handleMenuChange"
          />
        </div>

        <!-- ÜRÜN BİLGİLERİ -->
        <div ref="productInfoRef">
          <ProductInfo
            ref="productInfoComponentRef"
            v-model:productInfo="form"
            :isLoading="isProductDetailLoading"
            @saveChanges="saveProductInfoChanges"
            @requestUpdate="() => handleRequestUpdate('productInfo')"
            @hasChanges="() => markSectionDirty('productInfo')"
            @saved="() => clearSectionDirty('productInfo')"
          />
        </div>

        <!-- ÜRÜN GÖRSELLERİ -->
        <div ref="productImagesRef">
          <ProductImages
            :images="productImages"
            :isLoading="isProductDetailLoading"
            @requestUpdate="() => handleRequestUpdate('productImages')"
          />
        </div>

        <!-- SATIŞ & VARYANT BİLGİLERİ -->
        <div ref="salesVariantRef">
          <ProductDetailSaleVariant
            ref="salesVariantComponentRef"
            :isCategorySelected="true"
            :currencies="currencies"
            :selectedCurrency="selectedCurrencyCode"
            :category="getCategory"
            :productId="pid"
            :modelCode="getModelCode"
            :tableData="tableData"
            :isLoading="isProductDetailLoading"
            :attributes="getAttributes"
            :showOtherOptions="showOtherOptions"
            :isSaved="saleVariantIsSaved"
            :isVariant="isVariant"
            :groupId="productDetail?.groupId"
            :productDetail="productDetail"
            @requestUpdate="handleRequestUpdate"
            @saveChanges="handleSaleVariantSave"
            @hasChanges="() => markSectionDirty('salesVariant')"
            @saved="() => clearSectionDirty('salesVariant')"
          />
        </div>

        <!-- EK BİLGİLER -->
        <div ref="additionalInfoRef">
          <ProductDetailAdditionalInfo
            ref="additionalInfoComponentRef"
            :isLoading="isProductDetailLoading"
            :additionalInfo="additionalInfoData"
            @hasChanges="() => markSectionDirty('additionalInfo')"
            @saved="() => clearSectionDirty('additionalInfo')"
          />
        </div>

        <!-- ÜRÜN ÖZELLİKLERİ -->
        <div ref="productFeaturesRef">
          <ProductDetailProductFeatures
            :isCategorySelected="true"
            :isLoading="isProductDetailLoading"
            :productFeatures="productAttributes"
            @requestUpdate="() => handleRequestUpdate('productFeatures')"
          />
        </div>

        <!-- ÜRÜN AÇIKLAMASI -->
        <div ref="productDescriptionRef">
          <ProductDetailRichText
            ref="productDescriptionComponentRef"
            :content="productDescription"
            :descriptionEditable="descriptionEditable"
            :isLoading="isProductDetailLoading"
            @requestUpdate="() => handleRequestUpdate('productDescription')"
            @hasChanges="() => markSectionDirty('productDescription')"
            @saved="() => clearSectionDirty('productDescription')"
            
          />
        </div>

        <!-- TESLİMAT ŞABLONU -->
        <div ref="deliveryTemplateRef">
          <DetailDeliveryTemplate
            ref="deliveryTemplateComponentRef"
            :shipmentTemplateData="shipmentTemplateData"
            :isSaved="deliveryIsSaved"
            :isLoading="isProductDetailLoading"
            @resetSaveStatus="handleDeliveryTemplateResetSaveStatus"
            @saveChanges="handleDeliveryTemplateSave"
          />
        </div>

        <!-- ÜRÜN GÜVENLİĞİ BİLGİLERİ -->
        <div ref="productSafetySectionRef">
          <ProductSafetySection
            ref="productSafetyRef"
            :isLoading="isProductDetailLoading"
            :isSavingAllChanges="isSavingAllChanges"
            :getNutritionInfo="getCurrentNutritionInfo"
            :productId="pid"
            @hasChanges="() => markSectionDirty('productSafety')"
            @saved="() => clearSectionDirty('productSafety')"
          />
        </div>

        <div v-if="productDetail?.nutritionInfoRequired" ref="nutritionRateRef">
          <ProductDetailNutritionRateSection
            ref="nutritionRateSectionRef"
            :isLoading="isProductDetailLoading"
            :nutritionInfo="productSafetyInfo?.nutritionInfo"
            :productId="pid"
            :onSubmitAllSafetyInfo="handleSubmitAllSafetyInfo"
            @hasChanges="() => markSectionDirty('nutritionRate')"
          />
        </div>
      </main>
    </div>
    <div v-if="!isProductDetailLoading" class="delete-button-container">
      <Icons name="Delete" sm color="#5D3EBC" />
      <button class="delete-button" type="button" @click="openDeleteModal">
        Ürünü Sil
      </button>
    </div>
    <div v-else class="delete-button-skeleton-container">
      <Skeleton height="18px" width="76px" borderRadius="4px" />
    </div>

    <div class="actions-pane">
      <div class="actions-pane-container">
        <div class="left-section">
          <AppButton
            secondary
            md
            title="Güncelleme/Hata Talebi Geçmişini Gör"
            @click="handleShowFeedbackHistory"
          >
            <template #prependIcon>
              <Icons name="UpdateHistory" />
            </template>
          </AppButton>
        </div>
        <div class="right-section">
          <div class="apply-variants" v-if="!isProductDisabled">
            <input
              type="checkbox"
              id="apply-to-variants-checkbox"
              name="applyToVariants"
              v-model="localApplyToVariants"
            />
            <label class="label" for="apply-to-variants-checkbox">
              Değişiklikleri tüm varyantlara uygula.</label
            >
          </div>

          <div class="actions-row">
            <Button
              v-if="!isProductDisabled"
              primary
              md
              @click="handleSaveAllChanges"
              :disabled="isSaveAllChangesDisabled"
            >
              <span class="save-all-btn-content">
                <span v-if="isSavingAllChanges" class="spinner"></span>
                <Icons
                  v-else-if="isAllChangesSaved"
                  name="Checked"
                  color="#B4B4B4"
                  size="md"
                />
                {{ saveAllChangesButtonText }}
              </span>
            </Button>
            
            <a
              v-if="isProductDisabled"
              :href="helpCenterLink"
              target="_blank"
              class="help-center-button primary md"
            >
              <AppButton primary md title="Yardım Talebi Oluştur"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
    <UpdateHistoryModal
      ref="feedbackHistoryModalRef"
      :isProductDetailLoading="isProductDetailLoading"
    />
    <DeleteProductModal
      ref="deleteProductModalRef"
      page-name="detail"
      :product-id="pid"
    />

    <RequestUpdateModalForProductImages
      ref="productImagesModalRef"
      :images="productImages"
      :groupId="productDetail?.groupId"
    />
    <RequestUpdateModalForProductInfos ref="productInfoModalRef" />
    <RequestUpdateModalForProductFeatures ref="productFeaturesModalRef" />
    <RequestUpdateModalForProductDescription ref="productDescriptionModalRef" />
    <ProductDetailVideoPopup
      ref="videoPopupRef"
      videoUrl="https://magazadestek.n11.com/egitim-detay/n11-uru-n-detaylarina-nasil-ulas-ilir-22910"
      :productId="pid"
    />
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import Skeleton from '@/components/Skeleton/Skeleton';
import { useStore } from 'vuex';
import { Button, AppButton, Icons } from '@n11/so-ui-components';
import SideBar from './partial/SideBar.vue';
import ProductStatus from './partial/ProductStatus/ProductStatus.vue';
import ProductInfo from './partial/ProductInfo.vue';
import ProductImages from './partial/ProductImages.vue';
import ProductDetailSaleVariant from './partial/ProductDetailSaleVariant.vue';
import ProductDetailAdditionalInfo from './partial/ProductDetailAdditionalInfo.vue';
import ProductDetailProductFeatures from './partial/ProductDetailProductFeatures.vue';
import ProductDetailRichText from './partial/ProductDetailRichText.vue';
import DetailDeliveryTemplate from './partial/DetailDeliveryTemplate.vue';
import ProductSafetySection from './partial/ProductSafetySection.vue';
import ProductDetailNutritionRateSection from './partial/ProductDetailNutritionRateSection.vue';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import UpdateHistoryModal from './partial/HistoryModal.vue';
import RequestUpdateModalForProductFeatures from './partial/Modals/RequestUpdateModalForProductFeatures.vue';
import RequestUpdateModalForProductDescription from './partial/Modals/RequestUpdateModalForProductDescription.vue';
import DeleteProductModal from '@/components/common/DeleteProductModal/DeleteProductModal.vue';
import RequestUpdateModalForProductImages from './partial/Modals/RequestUpdateModalForProductImages.vue';
import RequestUpdateModalForProductInfos from './partial/Modals/RequestUpdateModalForProductInfos.vue';
import ProductDetailVideoPopup from './partial/Modals/ProductDetailVideoPopup.vue';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const props = defineProps({
  pid: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const productDetail = computed(
  () => store.getters['products/getProductDetail'],
);

const helpCenterLink = computed(() => {
  const productStatus = productDetail.value?.productStatus;
  if (productStatus === "CatalogRejected") {
    return "http://magazadestek.n11.com/sss?contentId=15305&openTicket=true";
  } else if (productStatus === "Prohibited") {
    return "http://magazadestek.n11.com/sss?contentId=15325&openTicket=true";
  }
});

const isProductDetailLoading = computed(
  () => store.getters['products/isProductDetailLoading'],
);
// const productDetailError = computed(
//   () => store.getters["products/getProductDetailError"]
// );
const productSafetyInfo = computed(
  () => store.getters['products/getProductSafetyInfo'],
);

const form = reactive({
  title: '',
  modelCode: '',
  category: '',
  stockCode: '',
  barcode: '',
  preparingTime: null,
  vatRate: null,
  listPrice: null,
  salePrice: null,
  quantity: null,
  applyToVariants: false,
});

watch(
  productDetail,
  (newVal) => {
    if (newVal) {
      form.title = newVal.title || '';
      form.modelCode = newVal.productMainId || '';
      form.category = newVal.categoryPath || '';
      form.stockCode = newVal.stockCode || '';
      form.barcode = newVal.barcode || '';
      form.preparingTime = newVal.preparingTime || null;
      form.vatRate = newVal.vatRate || null;
      form.listPrice = newVal.listPrice || null;
      form.salePrice = newVal.salePrice || null;
      form.quantity = newVal.quantity || null;
    }
  },
  { immediate: true },
);

const productImages = ref(
  computed(() => {
    if (productDetail.value?.images?.length) {
      return productDetail.value.images.map((url, index) => ({
        src: url,
        isCover: index === 0,
      }));
    }
  }),
);

const currencies = ref({});
const selectedCurrencyCode = ref(computed(() => productDetail.value?.currencyType === 'TL' ? 'TRY' : productDetail.value?.currencyType));
const productDescription = ref(computed(() => productDetail.value?.description));
const shipmentTemplateData = ref(
  computed(() => {
    return {
      name: productDetail.value?.shipmentTemplate,
      id: productDetail.value?.shipmentId,
    };
  }),
);

const descriptionEditable = computed(() => {
  return productDetail.value?.descriptionEditable;
});

const productStatusInfo = computed(() => ({
  saleStatusDisplayValue: productDetail.value?.saleStatusDisplayValue,
  saleStatus: productDetail.value?.saleStatus,
  saleTitleDisplayValue: productDetail.value?.saleTitleDisplayValue,
  productStatusDisplayValue: productDetail.value?.productStatusDisplayValue,
  productStatusColor: productDetail.value?.productStatusColor,
  productStatus: productDetail.value?.productStatus,
  rejectInfo: productDetail.value?.rejectInfo,
  isStatusEditable: productDetail.value?.isStatusEditable,
  prohibitedDescription: productDetail.value?.prohibitedDescription,
  prohibitedReason: productDetail.value?.prohibitedReason,
  inApprovalReason: productDetail.value?.inApprovalReason || [],
  productIds: productDetail.value?.id || [],
}));

const additionalInfoData = computed(() => {
  const rawOptions = productDetail.value?.customTextOptions || [];
  const customTextOptions = Array.isArray(rawOptions)
    ? rawOptions.map((text, idx) => ({ id: idx + 1, text }))
    : [];
  return {
    customTextOptions,
    maxPurchaseQuantity: productDetail.value?.maxPurchaseQuantity ?? null,
    productIds: productDetail.value?.id || [],
  };
});

const productAttributes = computed(() => productDetail.value?.attributes || []);
const getAttributes = computed(
  () =>
    productDetail.value?.attributes?.filter(
      (att) => att.attributeName !== 'Marka' && (att.isVariant || att.isSlicer),
    ) || [],
);
const showOtherOptions = computed(
  () =>
    productDetail.value?.attributes?.some((att) => att.isSlicer || att.isN11Grouping) || false,
);
const isVariant = computed(
  () => productDetail.value?.attributes?.some((att) => att.isVariant) || false,
);
const getCategory = computed(() => productDetail.value?.categoryId || 1);
const getModelCode = computed(() => productDetail.value?.productMainId || 1);

const tableData = reactive([
  {
    id: computed(() => productDetail.value?.id),
    attributes: computed(() => {
      if (productDetail.value?.attributes?.length) {
        return productDetail.value.attributes
          .filter((attr) => attr.isVariant || attr.isSlicer)
          .map((attr) => ({
            name: attr.attributeName,
            value: attr.attributeValue,
            isSlicer: attr.isSlicer,
          }));
      }
    }),
    name: computed(() => productDetail.value?.variantTitle),
    stockCode: computed(() => productDetail.value?.stockCode),
    gtin: computed(() => productDetail.value?.barcode),
    listPrice: computed(() => productDetail.value?.listPrice),
    salePrice: computed(() => productDetail.value?.salePrice),
    stock: computed(() => productDetail.value?.quantity),
    duration: computed(() => productDetail.value?.preparingTime),
    vatRate: computed(() => productDetail.value?.vatRate?.toString()),
    finalPrice: computed(() => productDetail.value?.finalPrice),
    subsidy: computed(() => productDetail.value?.subsidy),
    buyBoxPrice: computed(() => productDetail.value?.buyBoxPrice),
    commissionRate: computed(() => productDetail.value?.commissionRateValue),
    commissionAmount: computed(
      () => productDetail.value?.commissionAmountValue,
    ),
    hashPlain: computed(() => {
      if (productDetail.value?.attributes?.length) {
        const variantAttrs = productDetail.value.attributes.filter(
          (attr) => attr.isVariant,
        );
        return JSON.stringify(
          variantAttrs.map((attr) => ({
            name: attr.attributeName,
            value: attr.attributeValue,
          })),
        );
      }
    }),
    validations: {
      listPrice: {
        isDirty: false,
        error: false,
        message: null,
        valid: true,
        loading: false,
      },
      salePrice: {
        isDirty: false,
        error: false,
        message: null,
        valid: true,
        loading: false,
      },
      stock: {
        isDirty: false,
        error: false,
        message: null,
        valid: true,
        loading: false,
      },
      duration: {
        isDirty: false,
        error: false,
        message: null,
        valid: true,
        loading: false,
      },
      vatRate: {
        isDirty: false,
        error: false,
        message: null,
        valid: true,
        loading: false,
      },
    },
  },
]);

const productInformation = ref(null);
const localApplyToVariants = ref(false);
const deliveryIsSaved = ref(false);
const saleVariantIsSaved = ref(false);
const activeMenuIndex = ref(0);
const isSavingAllChanges = ref(false);
const isAllChangesSaved = ref(false);
const sectionDirty = reactive({
  productInfo: false,
  salesVariant: false,
  additionalInfo: false,
  deliveryTemplate: false,
  productDescription: false,
  productSafety: false,
  nutritionRate: false,
});

const hasAnyDirtySection = computed(() =>
  Object.values(sectionDirty).some(Boolean),
);
const isDirtyTrackingOn = ref(false);

const isSaveAllChangesDisabled = computed(
  () =>
    Boolean(isSavingAllChanges.value) ||
    Boolean(isAllChangesSaved.value) ||
    !Boolean(hasAnyDirtySection.value),
);

const saveAllChangesButtonText = computed(() =>
  isAllChangesSaved.value ? 'Değişiklikler Kaydedildi' : 'Tüm Değişiklikleri Kaydet',
);

const productInfoComponentRef = ref(null);
const salesVariantComponentRef = ref(null);
const additionalInfoComponentRef = ref(null);
const productDescriptionComponentRef = ref(null);
const deliveryTemplateComponentRef = ref(null);

// Template refs for scrolling
const productStatusRef = ref(null);
const productInfoRef = ref(null);
const productImagesRef = ref(null);
const salesVariantRef = ref(null);
const additionalInfoRef = ref(null);
const productFeaturesRef = ref(null);
const productDescriptionRef = ref(null);
const deliveryTemplateRef = ref(null);
const productSafetyRef = ref(null);
const productSafetySectionRef = ref(null);
const nutritionRateRef = ref(null);
const nutritionRateSectionRef = ref(null);
const feedbackHistoryModalRef = ref(null);
const deleteProductModalRef = ref(null);
const pageRef = ref(null);
const videoPopupRef = ref(null);

const markSectionDirty = (sectionKey) => {
  if (!isDirtyTrackingOn.value) return;
  if (!Object.prototype.hasOwnProperty.call(sectionDirty, sectionKey)) return;
  sectionDirty[sectionKey] = true;
  if (sectionKey === 'salesVariant') saleVariantIsSaved.value = false;
  if (sectionKey === 'deliveryTemplate') deliveryIsSaved.value = false;
  if (isAllChangesSaved.value) isAllChangesSaved.value = false;
};

const clearSectionDirty = (sectionKey) => {
  if (!Object.prototype.hasOwnProperty.call(sectionDirty, sectionKey)) return;
  sectionDirty[sectionKey] = false;
  if (!hasAnyDirtySection.value) {
    isAllChangesSaved.value = true;
  }
};

const clearAllDirty = () => {
  Object.keys(sectionDirty).forEach((k) => {
    sectionDirty[k] = false;
  });
};

const handleDeliveryTemplateResetSaveStatus = () => {
  deliveryIsSaved.value = false;
  markSectionDirty('deliveryTemplate');
};

// Modal refs for each section
const productInfoModalRef = ref(null);
const productImagesModalRef = ref(null);
const productFeaturesModalRef = ref(null);
const productDescriptionModalRef = ref(null);

const getCurrentNutritionInfo = () => {
  if (nutritionRateSectionRef.value?.getCurrentNutritionInfo) {
    return nutritionRateSectionRef.value.getCurrentNutritionInfo();
  }
  return null;
};

const handleSubmitAllSafetyInfo = async (nutritionInfoPayload) => {
  return productSafetyRef.value?.submitAllSafetyInfo?.(nutritionInfoPayload);
};

// Menu index to ref mapping
const menuRefs = {
  0: productStatusRef, // Ürün & Satış Durumu
  1: productInfoRef, // Ürün Bilgileri
  2: productImagesRef, // Ürün Görselleri
  3: salesVariantRef, // Satış & Varyant Bilgileri
  4: additionalInfoRef, // Ek Bilgiler
  5: productFeaturesRef, // Ürün Özellikleri
  6: productDescriptionRef, // Ürün Açıklaması
  7: deliveryTemplateRef, // Teslimat Şablonu
  8: productSafetySectionRef, // Ürün Güvenliği Bilgileri
  9: nutritionRateRef, // Besin Bilgileri ve Değerleri
};

watch(
  () => props.pid,
  async (newId) => {
    if (newId) {
      localApplyToVariants.value = false;
      isAllChangesSaved.value = false;
      isSavingAllChanges.value = false;
      isDirtyTrackingOn.value = false;
      clearAllDirty();

      store.commit('products/SET_PRODUCT_DETAIL_DATA', null);
      store.commit('products/SET_PRODUCT_DETAIL_LOADING', true);
      try {
        const result = await store.dispatch(
          'products/fetchProductDetail',
          newId,
        );

        if (!result || result.error) {
          throw new Error(result?.error || 'Failed to fetch product detail');
        }

        productInformation.value = result;

        await store.dispatch('products/fetchProductSafetyInfo', newId);

        await nextTick();
        isDirtyTrackingOn.value = true;
      } catch (error) {
        console.error('Error fetching product data:', error);

        store.commit('common/SET_TOASTER_DATA', {
          type: ToastMessageTypes.ERROR,
          title: 'Hata',
          description:
            'Ürün detayları yüklenirken bir hata oluştu. Lütfen tekrar deneyin.',
        });

        if (window.pdObserver) {
          window.pdObserver.disconnect();
          delete window.pdObserver;
        }

        setTimeout(() => {
          window.location.href = '/selleroffice/v2/product/products';
        }, 1000);
      }
    }
  },
  {
    immediate: true,
  },
);

async function saveProductInfoChanges(data) {
  try {
    await store.dispatch('products/updateProductModelCode', {
      productId: props.pid,
      modelCode: data.modelCode,
    });
  } catch (error) {
    console.error('Model kodu güncellenirken hata oluştu:', error);
  }
}

const getExchanges = async () => {
  try {
    const response = await store.dispatch('categories/getExchangeRates');
    currencies.value = response;
  } catch (error) {
    console.error('Exchange rates yüklenemedi:', error);
    return [];
  }
};

function handleMenuChange(index) {
  activeMenuIndex.value = index;
  const targetRef = menuRefs[index];
  if (targetRef && targetRef.value) {
    targetRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }
}

async function handleRefetchProductDetail() {
  if (props.pid) {
    try {
      await store.dispatch('products/fetchProductDetail', props.pid);
    } catch (error) {
      console.error('Error refetching product detail:', error);
    }
  }
}

const openDeleteModal = () => {
  sendProductDetailEvent(GA4_EVENT.DELETE_PRODUCT, {
    button_name: 'Ürünü Sil',
    button_type: 'Delete Product',
    button_area: 'Page End',
    item_id: props.pid ?? '',
  });
  deleteProductModalRef?.value?.openDeleteModal();
};

function handleRequestUpdate(source) {
  // Modal ref mapping
  const modalRefs = {
    productInfo: productInfoModalRef,
    productImages: productImagesModalRef,
    productFeatures: productFeaturesModalRef,
    productDescription: productDescriptionModalRef,
  };

  const buttonAreaMapping = {
    productInfo: 'Product Information',
    productImages: 'Product Image',
    productFeatures: 'Product Specifications',
    productDescription: 'Product Description',
  };

  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Güncelleme Talep Et / Hata Bildir',
    button_type: 'Request Update / Report a Bug',
    button_area: buttonAreaMapping[source] || source,
    item_id: props.pid,
  });

  const modalRef = modalRefs[source];
  if (modalRef?.value) {
    modalRef.value.openModal(source);
  }
}

function handleShowFeedbackHistory() {
  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Güncelleme/Hata Talebi Geçmişini Gör',
    button_type: 'Request / Bug History',
    button_area: 'Page End',
    item_id: props.pid,
  });
  if (feedbackHistoryModalRef.value) {
    feedbackHistoryModalRef.value.openModal();
  }
}

async function handleDeliveryTemplateSave(templateData) {
  try {
    const currentId = props.pid;
    await store.dispatch(
      'products/getSubmittingShipmentTemplate',
      {
        productId: currentId,
        payload: templateData,
      },
    );
    deliveryIsSaved.value = true;
    clearSectionDirty('deliveryTemplate');
  } catch (err) {
    deliveryIsSaved.value = false;
    console.log('err', err);
  }
}

async function handleSaleVariantSave(templateData) {
  try {
    const response = await store.dispatch(
      'products/getSubmittingSaleVariant',
      templateData,
    );

    const allErrorMessages = [];
    let hasFailure = false;
    response.forEach((item) => {
      if (!item.success) {
        hasFailure = true;
        if (item.errors && item.errors.length > 0) {
          item.errors.forEach((error) => {
            allErrorMessages.push(
              `Stok Kodu: ${item.sellerStockCode} - ${error.errorMessage}`,
            );
          });
        }
      }
    });

    if (hasFailure) {
      saleVariantIsSaved.value = false;
      const combinedErrorMessage = allErrorMessages.join('\n');
      store.commit('common/SET_TOASTER_DATA', {
        type: ToastMessageTypes.ERROR,
        title: '',
        description: combinedErrorMessage,
      });

      salesVariantComponentRef.value?.revertStockAndDurationToInitial?.();
    } else {
      saleVariantIsSaved.value = true;
      clearSectionDirty('salesVariant');
      store.commit('common/SET_TOASTER_DATA', {
        type: ToastMessageTypes.SUCCESS,
        title: 'Başarılı',
        description: 'Yaptığınız değişiklikler kaydedilmiştir.',
      });
    }
  } catch (err) {
    saleVariantIsSaved.value = false;
    salesVariantComponentRef.value?.revertStockAndDurationToInitial?.();
    console.log('err', err);
  }
}

async function handleSaveAllChanges() {
  if (isSavingAllChanges.value) return;

  isSavingAllChanges.value = true;

  try {
    // collect information from all sections
    const productInfoData =
      productInfoComponentRef.value?.getProductInformation();
    const salesVariantInfoData =
      await salesVariantComponentRef.value?.getSalesVariant();
    const additionalInfoData =
      additionalInfoComponentRef.value?.getAdditionalInfo();
    const deliveryTemplateData =
      deliveryTemplateComponentRef.value?.getDeliveryTemplate();
    const productDescriptionData =
      productDescriptionComponentRef.value?.getRichText();
    const productSafetyInfoData = productSafetyRef.value?.getAllSafetyInfoData(
      getCurrentNutritionInfo(),
    );

    // validation checks
    const hasCriticalErrors =
      !productInfoData?.isValid ||
      !salesVariantInfoData?.isValid ||
      !deliveryTemplateData?.isValid ||
      !productDescriptionData?.isValid;

    if (hasCriticalErrors) {
      let targetRef = null;
      if (!productInfoData?.isValid) {
        targetRef = productInfoRef;
      } else if (!salesVariantInfoData?.isValid) {
        targetRef = salesVariantRef;
      } else if (!deliveryTemplateData?.isValid) {
        targetRef = deliveryTemplateRef;
      } else if (!productDescriptionData?.isValid) {
        targetRef = productDescriptionRef;
      }

      if (targetRef?.value) {
        targetRef.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      isSavingAllChanges.value = false;
      return;
    }

    // build payload
    const payload = {
      changesAll: Boolean(localApplyToVariants.value),
      productStatus: productDetail.value?.productStatus,
      modelCode: productInfoData?.data?.modelCode,
    };

    // sale variant - products array
    if (salesVariantInfoData?.data?.tableRows) {
      const formattedProducts =
        salesVariantComponentRef.value?.formatTableRowsForPayload(
          salesVariantInfoData.data.tableRows,
        );
      if (formattedProducts?.products?.length > 0) {
        payload.products = formattedProducts.products;
      }
    }

    // additional info
    if (additionalInfoData?.data) {
      payload.hasCustomTextOptions =
        additionalInfoData.data.hasCustomTextOptions ?? false;
      payload.customTextOptions =
        additionalInfoData.data.customTextOptions ?? [];
      payload.hasMaxPurchaseQuantity =
        additionalInfoData.data.hasMaxPurchaseQuantity ?? false;
      payload.maxPurchaseQuantity =
        additionalInfoData.data.maxPurchaseQuantity ?? null;
    }

    // delivery template
    if (deliveryTemplateData?.data?.selectedTemplate) {
      payload.templateId = Number(
        deliveryTemplateData.data.selectedTemplate.id,
      );
      payload.templateName = deliveryTemplateData.data.selectedTemplate.name;
    }

    // product description
    if (productDescriptionData?.data?.description) {
      payload.description = productDescriptionData.data.description;
    }
    if (productDescriptionData?.data?.templateName) {
      payload.descriptionTemplateName =
        productDescriptionData.data.templateName;
    }

    // product safety info
    if (productSafetyInfoData?.productSafetyRequest) {
      payload.productSafetyRequest = productSafetyInfoData.productSafetyRequest;
    }

    await store.dispatch('products/updateAllProductFields', {
      productId: props.pid,
      payload,
    });

    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.SUCCESS,
      title: 'Başarılı',
      description: 'Yaptığınız değişiklikler kaydedilmiştir.',
    });

    sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
      button_name: 'Değişiklikleri Kaydet',
      button_type: 'Save Changes',
      button_area: 'Page End',
      item_id: props.pid,
      all_variants_checkbox: localApplyToVariants.value,
    });

    const dirtySnapshot = { ...sectionDirty };

    isAllChangesSaved.value = true;
    clearAllDirty();

    if (dirtySnapshot.salesVariant) {
      saleVariantIsSaved.value = true;
      salesVariantComponentRef.value?.showSaveAllSuccessState?.();
    }
    productDescriptionComponentRef.value?.resetDirtyState?.({
      showSuccessState: Boolean(dirtySnapshot.productDescription),
      savedContent: productDescriptionData?.data?.description ?? '',
    });
    if (dirtySnapshot.deliveryTemplate) {
      deliveryIsSaved.value = true;
    }

    await handleRefetchProductDetail();
    await store.dispatch('products/fetchProductSafetyInfo', props.pid);

    if (dirtySnapshot.productInfo) {
      productInfoComponentRef.value?.showSaveAllSuccessState?.();
    }
    if (dirtySnapshot.additionalInfo) {
      additionalInfoComponentRef.value?.showSaveAllSuccessState?.();
    }
    if (dirtySnapshot.productSafety) {
      productSafetyRef.value?.showSaveAllSuccessState?.();
    }
    if (dirtySnapshot.nutritionRate) {
      nutritionRateSectionRef.value?.showSaveAllSuccessState?.();
    }
  } catch (error) {
    const errorMessage =
      error?.data?.errors?.[0]?.message ||
      error?.data?.message ||
      error?.message ||
      'Bir hata oluştu. Lütfen tekrar deneyin.';

    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      title: 'Hata',
      description: errorMessage,
    });

    salesVariantComponentRef.value?.revertStockAndDurationToInitial?.();
  } finally {
    isSavingAllChanges.value = false;
  }
}

onMounted(async () => {
  await getExchanges();
  videoPopupRef.value?.openPopup();

  const observerOptions = {
    root: null,
    rootMargin: "-10% 0px -50% 0px",
    threshold: [0, 0.1, 0.25, 0.5],
  };

  const observerCallback = () => {
    const activeSections = [];

    Object.keys(menuRefs).forEach((index) => {
      const sectionRef = menuRefs[index];
      if (sectionRef.value) {
        const rect = sectionRef.value.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const activeZoneTop = viewportHeight * 0.1;
        const activeZoneBottom = viewportHeight * 0.5;

        const visibleTop = Math.max(rect.top, activeZoneTop);
        const visibleBottom = Math.min(rect.bottom, activeZoneBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const sectionHeight = rect.height;

        const visibilityRatio =
          sectionHeight > 0 ? visibleHeight / sectionHeight : 0;

        const isInActiveZone =
          visibilityRatio > 0.1 ||
          (rect.top >= activeZoneTop && rect.top < activeZoneBottom);

        if (isInActiveZone) {
          activeSections.push({
            index: parseInt(index),
            visibilityRatio,
            top: rect.top,
          });
        }
      }
    });

    if (activeSections.length > 0) {
      activeSections.sort((a, b) => a.index - b.index);
      activeMenuIndex.value = activeSections[0].index;
    }
  };

  const observer = new IntersectionObserver(observerCallback, observerOptions);
  Object.values(menuRefs).forEach((sectionRef) => {
    if (sectionRef.value) {
      observer.observe(sectionRef.value);
    }
  });
  window.pdObserver = observer;
});

onUnmounted(() => {
  if (window.pdObserver) {
    window.pdObserver.disconnect();
    delete window.pdObserver;
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.header-title {
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.2px;
  margin-bottom: 8px;
}

.subtitle {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.05px;
}

.product-detail-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  min-height: 100vh;
  box-sizing: border-box;
  margin-bottom: 72px;
}

.content-grid {
  display: flex;
  gap: 24px;
}

/* Content */
.pd-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.delete-button-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;

  .delete-button {
    color: $colorM50;
    font-size: 13px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
    padding: 0;
  }
}
.delete-button-skeleton-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  align-self: stretch;
  padding-top: 8px;
}
/* Section / Card */
.section {
  width: 100%;
}
.card {
  border-radius: 8px;
  background: $colorN00;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px dashed $gray-26;
}
.section-tag {
  background: $colorM05;
  color: $colorM50;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 6px;
}
.header-actions .link {
  background: transparent;
  border: none;
  color: $colorN60;
  cursor: pointer;
}

/* Inner padding */
.card-inner {
  padding: 18px 20px;
}

.btn {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid $gray-27;
  cursor: pointer;
  background: $colorN00;
}
.btn-outline {
  border-color: #cfc7ff;
  color: $colorM50;
}
.btn-primary {
  background: $colorM50;
  color: $colorN00;
  border: none;
}

.actions-pane {
  z-index: 10;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  position: fixed;

  border-top: 0.5px solid $colorN30;
  background: $colorN00;
  align-items: center;
  .actions-pane-container {
    width: 1320px;
    display: flex;
    justify-content: space-between;
    padding: 16px 0;
  }
  .left-section {
    .icon {
      padding-right: 3px;
    }
    :deep(.icon.md svg) {
      width: 18px !important;
      height: 18px !important;
    }
  }
  .right-section {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    .apply-variants {
      display: flex;
      gap: 8px;
      Input {
        width: 16px;
        height: 16px;
        margin: 0;
        accent-color: $colorN80;
      }
      .label {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
      }
    }
    .help-center-button {
      display: inline-block;
      width: 193px;
      text-decoration: none;
      cursor: pointer;
      :deep(button) {
        width: 100%;
        padding: 11px 20px;
      }
    }
  }
}

.save-all-btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: $colorN00;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* küçük responsive */
@media (max-width: 960px) {
  .product-detail-page {
    flex-direction: column;
    padding: 12px;
  }
  .content-grid {
    flex-direction: column;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .status-grid {
    grid-template-columns: 1fr;
  }
}

/* Mobile layout düzeltmesi */
@media (max-width: 768px) {
  .product-detail-page {
    padding: 12px;
  }

  .content-grid {
    flex-direction: column;
    gap: 0;
  }
}
</style>
